import type { WatermarkSummary } from 'fym-shared/src/music24/watermarkUpsell';

const STORAGE_KEY = 'm24.upsell.summaryBeforePayment';

/** What the watermark summary said before the payment: the facts that the payment return page needs. */
export type PaymentWatermarkSummary = Pick<WatermarkSummary, 'files' | 'cleanCopiesAvailable'>;

/**
 * Keeps the last watermark summary of a plan with the watermark across the Stripe trip, in the tab
 * (`sessionStorage`). After the payment the server answers zeros, so the payment return page cannot
 * read from the summary if a clean-copies run will start. A browser that blocks the storage loses
 * only the short wait for the run on that page.
 */
export function rememberPaymentWatermarkSummary(summary: PaymentWatermarkSummary): void {
  try {
    const kept: PaymentWatermarkSummary = {
      files: summary.files,
      cleanCopiesAvailable: summary.cleanCopiesAvailable,
    };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(kept));
  } catch (error) {
    console.error(error);
  }
}

/** The summary of before the payment, or null when the tab has none. */
export function readPaymentWatermarkSummary(): PaymentWatermarkSummary | null {
  try {
    const value = window.sessionStorage.getItem(STORAGE_KEY);
    if (value === null) {
      return null;
    }
    const parsed = JSON.parse(value) as Partial<PaymentWatermarkSummary> | null;
    if (parsed === null || typeof parsed.files !== 'number' || typeof parsed.cleanCopiesAvailable !== 'boolean') {
      return null;
    }
    return { files: parsed.files, cleanCopiesAvailable: parsed.cleanCopiesAvailable };
  } catch (error) {
    console.error(error);
    return null;
  }
}

/** Removes the summary: the payment return page got its run, or stopped the wait for it. */
export function forgetPaymentWatermarkSummary(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error(error);
  }
}
